import React, { useState, useEffect, useRef } from 'react';
import { FileCode, ChevronRight, Check, AlertCircle, Loader } from 'lucide-react';
import theme from '../theme';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Batch 1B canonical repair — normalize legacy template identifiers so the
// wizard's SMART_CONTRACT_TEMPLATES lookup succeeds even when older drafts
// persisted tokens like `tpl-erc721-basic-v1` or `erc-721`.
function normalizeTemplateType(raw) {
  if (!raw || typeof raw !== 'string') return '';
  const s = raw.toLowerCase().replace(/[^a-z0-9]/g, '');
  if (s.includes('erc721a')) return 'ERC721A';
  if (s.includes('erc721')) return 'ERC721';
  if (s.includes('erc1155')) return 'ERC1155';
  if (s.includes('bep721')) return 'BEP721';
  if (s.includes('bep1155')) return 'BEP1155';
  return raw;
}

// Match a normalized key against the templates map (case-insensitive fallback).
function resolveTemplateKey(templatesMap, rawKey) {
  const norm = normalizeTemplateType(rawKey);
  if (!norm) return '';
  if (templatesMap && templatesMap[norm]) return norm;
  const lower = norm.toLowerCase();
  const match = Object.keys(templatesMap || {}).find(
    (k) => k.toLowerCase() === lower
  );
  return match || norm;
}

// Batch 1B canonical repair — map legacy draft.params keys to the
// SMART_CONTRACT_TEMPLATES field keys the wizard renders. Older drafts may
// store token_name / token_symbol / royalty_percent; the ERC721 field
// schema uses `name` / `symbol` / `royalty_percent` / etc. Merge both.
function normalizeParams(params) {
  if (!params || typeof params !== 'object') return {};
  const p = { ...params };
  const alias = (k, aliases) => {
    if (p[k] !== undefined && p[k] !== null && p[k] !== '') return;
    for (const a of aliases) {
      if (params[a] !== undefined && params[a] !== null && params[a] !== '') {
        p[k] = params[a];
        return;
      }
    }
  };
  alias('name', ['token_name', 'contract_name']);
  alias('symbol', ['token_symbol']);
  alias('max_supply', ['maxSupply', 'supply']);
  alias('mint_price', ['mintPrice', 'price']);
  alias('base_uri', ['baseURI', 'baseUri', 'metadata_uri']);
  alias('royalty_percent', ['royalty', 'royalty_bps']);
  return p;
}

/**
 * SmartContractWizard — Batch 1B canonical source-of-truth repair.
 *
 * Explicit state machine:
 *   LOADING   — templates fetch and (if editing) draft fetch are in flight
 *   NO_DRAFT  — templates loaded, no existing draft for this portfolio
 *   HAS_DRAFT — templates + normalized draft loaded; banner + form
 *               render from the SAME `existingDraft` object
 *   ERROR     — templates or draft load failed; user can retry
 *
 * Draft source of truth = `contract_drafts` collection reached via
 *   GET /api/contracts/drafts/{id}   (hydrate)
 *   PATCH /api/contracts/drafts/{id} (update in place)
 * A NEW draft goes through /api/contract-templates/generate which mirrors
 * into contract_drafts with the same id.
 */
const SmartContractWizard = ({
  onComplete,
  portfolioType,
  portfolioId,
  initialDraftId,
  initialTemplateType,
  initialContractData,
}) => {
  const [phase, setPhase] = useState('LOADING');
  const [templates, setTemplates] = useState({});
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [contractData, setContractData] = useState({});
  const [existingDraftId, setExistingDraftId] = useState(initialDraftId || null);
  const [existingDraft, setExistingDraft] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [genErr, setGenErr] = useState('');
  const [genSuccess, setGenSuccess] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);

  // Keep onComplete in a ref so hydration effect never fires on unrelated
  // parent re-renders. This is the fix for the previous infinite-render
  // that wiped user edits.
  const onCompleteRef = useRef(onComplete);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  // Single loader: fetch templates + (optionally) draft in parallel. Runs
  // once per draft id (or explicit retry). Do NOT re-hydrate on user edits.
  useEffect(() => {
    let cancelled = false;
    setPhase('LOADING');
    setGenErr('');

    const token = localStorage.getItem('token');
    const templatesReq = fetch(`${BACKEND_URL}/api/contract-templates/list`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('templates_failed'))))
      .then((d) => d.templates || {});

    const draftReq = initialDraftId
      ? fetch(`${BACKEND_URL}/api/contracts/drafts/${initialDraftId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        })
          .then((r) => {
            if (r.status === 404) return null; // legacy pointer with no draft row
            if (!r.ok) throw new Error('draft_failed');
            return r.json();
          })
          .then((d) => (d ? d.draft || d : null))
      : Promise.resolve(null);

    Promise.all([templatesReq, draftReq])
      .then(([tpls, draft]) => {
        if (cancelled) return;
        setTemplates(tpls || {});

        if (draft) {
          const rawKey = draft.template_type || draft.template_id || '';
          const key = resolveTemplateKey(tpls || {}, rawKey);
          const params = normalizeParams(draft.params || draft.contract_data);

          const normalized = {
            id: draft.id || initialDraftId,
            portfolio_id: draft.portfolio_id || null,
            creator_id: draft.creator_id || null,
            standard: draft.standard || key,
            template_id: key,
            template_version: draft.template_version || null,
            params,
            status: draft.status || 'DRAFT',
            created_at: draft.created_at || null,
            updated_at: draft.updated_at || null,
            deployed: !!draft.deployed,
          };
          setExistingDraft(normalized);
          setExistingDraftId(normalized.id);
          setSelectedTemplate(key);
          setContractData(params);
          setPhase('HAS_DRAFT');

          // Notify parent ONCE with normalized identity so Step 5 summary
          // + Kickback detail see the same canonical draft.
          if (onCompleteRef.current) {
            onCompleteRef.current({
              contract_id: normalized.id,
              template_type: key,
              contract_data: params,
              existing: true,
            });
          }
        } else {
          // No existing draft: reset state and enter NO_DRAFT so the user
          // can pick a standard. Do NOT fall back here mid-load.
          setExistingDraft(null);
          setExistingDraftId(null);
          if (initialTemplateType) {
            const k = resolveTemplateKey(tpls || {}, initialTemplateType);
            setSelectedTemplate(k || '');
            setContractData(normalizeParams(initialContractData));
          } else {
            setSelectedTemplate('');
            setContractData({});
          }
          setPhase('NO_DRAFT');
        }
      })
      .catch(() => {
        if (cancelled) return;
        setPhase('ERROR');
      });

    return () => {
      cancelled = true;
    };
  }, [initialDraftId, reloadTick]);

  const updateField = (key, value) => {
    setContractData((prev) => ({ ...prev, [key]: value }));
  };

  const handleGenerate = async () => {
    const token = localStorage.getItem('token');
    setGenErr('');
    setGenSuccess(false);
    setGenerating(true);
    try {
      // HAS_DRAFT → PATCH same draft id (no dup, no extra quota).
      // NO_DRAFT → POST /contract-templates/generate which mirrors into
      // contract_drafts with the same id (single canonical row).
      const url = existingDraftId
        ? `${BACKEND_URL}/api/contracts/drafts/${existingDraftId}`
        : `${BACKEND_URL}/api/contract-templates/generate`;
      const method = existingDraftId ? 'PATCH' : 'POST';
      const body = existingDraftId
        ? { params: contractData, portfolio_id: portfolioId || null }
        : {
            template_type: selectedTemplate,
            contract_data: contractData,
            portfolio_id: portfolioId || null,
          };
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      if (response.ok) {
        const data = await response.json();
        const draftId =
          data.draft_id || data.contract_id || data.draft?.id || existingDraftId;
        setExistingDraftId(draftId);
        // Update the local canonical draft so the banner reflects new values.
        setExistingDraft((prev) =>
          prev
            ? { ...prev, id: draftId, params: contractData, updated_at: new Date().toISOString() }
            : {
                id: draftId,
                portfolio_id: portfolioId || null,
                standard: selectedTemplate,
                template_id: selectedTemplate,
                params: contractData,
                status: 'DRAFT',
                deployed: false,
              }
        );
        setPhase('HAS_DRAFT');
        setGenSuccess(true);
        if (onComplete) {
          onComplete({
            contract_id: draftId,
            template_type: selectedTemplate,
            contract_data: contractData,
            existing: false,
          });
        }
      } else {
        let msg = 'Failed to save contract draft.';
        try {
          const err = await response.json();
          if (err?.detail?.validation_errors) {
            msg = 'Please fix the highlighted fields.';
          } else if (typeof err?.detail === 'string') {
            msg = err.detail;
          }
        } catch (_) {
          /* ignore */
        }
        setGenErr(msg);
      }
    } catch (error) {
      console.error('Contract generation error:', error);
      setGenErr('Network error while saving contract. Your entries are preserved.');
    } finally {
      setGenerating(false);
    }
  };

  const isComplete = () => {
    if (!selectedTemplate) return false;
    const template = templates[selectedTemplate];
    if (!template || !Array.isArray(template.fields)) return false;
    // Guided royalty validation (0–15) mirrors backend range check.
    const roy = contractData.royalty_percent;
    if (roy !== undefined && roy !== '' && roy !== null) {
      const n = Number(roy);
      if (Number.isNaN(n) || n < 0 || n > 15) return false;
    }
    return template.fields.every((field) => {
      if (field.required) {
        return contractData[field.key] && contractData[field.key].toString().trim() !== '';
      }
      return true;
    });
  };

  // Live-compute ERC-1155 Total Possible Supply for the Creator.
  const totalPossibleSupply = (() => {
    if (!selectedTemplate) return null;
    if (!selectedTemplate.toUpperCase().includes('1155')) return null;
    const t = Number(contractData.token_ids || 0);
    const m = Number(contractData.max_supply_per_id || 0);
    if (t > 0 && m > 0) return t * m;
    return null;
  })();

  // ==================== RENDER — explicit state machine ====================

  if (phase === 'LOADING') {
    return (
      <div
        data-testid="smart-contract-loading"
        style={{
          background: theme.colors.bg.secondary,
          padding: '32px',
          borderRadius: theme.radius.md,
          border: `1px solid ${theme.colors.borders}`,
          textAlign: 'center',
          color: theme.colors.text.secondary,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <Loader size={28} style={{ animation: 'spin 1s linear infinite', color: theme.colors.primary }} />
        <div style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text.primary }}>
          Loading contract details…
        </div>
      </div>
    );
  }

  if (phase === 'ERROR') {
    return (
      <div
        data-testid="smart-contract-error-state"
        style={{
          background: '#FEF2F2',
          padding: '24px',
          borderRadius: theme.radius.md,
          border: `1px solid #FCA5A5`,
          color: '#7F1D1D',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12,
        }}
      >
        <AlertCircle size={22} style={{ marginTop: 2 }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 6 }}>
            Could not load contract details
          </div>
          <div style={{ fontSize: 13, marginBottom: 12 }}>
            The saved contract draft failed to load. Your existing draft has NOT been changed.
          </div>
          <button
            data-testid="smart-contract-retry-btn"
            onClick={() => setReloadTick((n) => n + 1)}
            style={{
              padding: '8px 16px',
              background: '#DC2626',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: theme.radius.sm,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const template = selectedTemplate ? templates[selectedTemplate] : null;

  return (
    <div>
      {/* Existing draft banner — derives from the SAME `existingDraft` object
          that also drives the form below (Section D1 canonical rule). */}
      {phase === 'HAS_DRAFT' && existingDraft && (
        <div
          data-testid="contract-existing-banner"
          style={{
            marginBottom: '16px',
            padding: '16px',
            background: '#EEF2FF',
            border: '1px solid #6366F1',
            borderRadius: theme.radius.sm,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <FileCode size={20} style={{ color: '#4F46E5' }} />
          <div>
            <p style={{ fontSize: '14px', fontWeight: 600, color: '#3730A3', margin: 0 }}>
              Existing Contract Draft
            </p>
            <p style={{ fontSize: '13px', color: '#4338CA', margin: 0 }}>
              A saved contract draft is connected to this Kickback.
              {template?.name ? ` Standard: ${template.name}` : ''}
            </p>
          </div>
        </div>
      )}

      <div
        style={{
          background: theme.colors.bg.secondary,
          padding: '24px',
          borderRadius: theme.radius.md,
          border: `1px solid ${theme.colors.borders}`,
        }}
      >
        <h3
          style={{
            fontSize: '18px',
            fontWeight: 700,
            color: theme.colors.text.primary,
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <FileCode size={24} style={{ color: theme.colors.primary }} />
          Smart Contract Builder
        </h3>
        <p
          style={{
            fontSize: '13px',
            color: theme.colors.text.secondary,
            marginBottom: '24px',
          }}
        >
          {phase === 'HAS_DRAFT'
            ? 'Review or update your saved contract configuration.'
            : 'Select a contract template and configure your NFT smart contract.'}
        </p>

        {/* NO_DRAFT: pick a standard. Never render this while LOADING. */}
        {phase === 'NO_DRAFT' && !selectedTemplate && (
          <div>
            <h4
              style={{
                fontSize: '14px',
                fontWeight: 600,
                color: theme.colors.text.primary,
                marginBottom: '16px',
              }}
            >
              Choose Contract Standard
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {Object.entries(templates).map(([key, tpl]) => (
                <button
                  key={key}
                  data-testid={`contract-template-${key}`}
                  onClick={() => setSelectedTemplate(key)}
                  style={{
                    padding: '16px',
                    border: `2px solid ${theme.colors.borders}`,
                    borderRadius: theme.radius.sm,
                    background: '#FFFFFF',
                    textAlign: 'left',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = theme.colors.primary;
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = theme.colors.borders;
                  }}
                >
                  <div
                    style={{
                      fontSize: '15px',
                      fontWeight: 600,
                      color: theme.colors.text.primary,
                      marginBottom: '4px',
                    }}
                  >
                    {tpl.name}
                  </div>
                  <div style={{ fontSize: '13px', color: theme.colors.text.secondary }}>
                    {tpl.description}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* HAS_DRAFT or NO_DRAFT+picked: configuration form. */}
        {selectedTemplate && template && (
          <div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '24px',
              }}
            >
              <h4
                data-testid="contract-config-heading"
                style={{ fontSize: '14px', fontWeight: 600, color: theme.colors.text.primary }}
              >
                {template.name} Configuration
              </h4>
              {phase !== 'HAS_DRAFT' && (
                <button
                  data-testid="contract-change-template-btn"
                  onClick={() => {
                    setSelectedTemplate('');
                    setContractData({});
                  }}
                  style={{
                    padding: '6px 12px',
                    border: `1px solid ${theme.colors.borders}`,
                    borderRadius: theme.radius.sm,
                    background: '#FFFFFF',
                    fontSize: '12px',
                    cursor: 'pointer',
                    color: theme.colors.text.secondary,
                  }}
                >
                  Change Template
                </button>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {template.fields.filter(f => f.key !== 'base_uri').map((field) => {
                const isRoyalty = field.key === 'royalty_percent';
                return (
                  <div key={field.key}>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '13px',
                        fontWeight: 600,
                        color: theme.colors.text.primary,
                        marginBottom: '6px',
                      }}
                    >
                      {field.label}{' '}
                      {field.required && <span style={{ color: 'red' }}>*</span>}
                    </label>
                    {field.description && (
                      <p data-testid={`contract-field-${field.key}-helper`} style={{
                        fontSize: '12px',
                        color: theme.colors.text.secondary,
                        marginTop: 0,
                        marginBottom: 6,
                        lineHeight: 1.5,
                      }}>
                        {field.description}
                      </p>
                    )}
                    <input
                      data-testid={`contract-field-${field.key}`}
                      type={field.type}
                      min={isRoyalty ? 0 : undefined}
                      max={isRoyalty ? 15 : undefined}
                      step={isRoyalty ? '0.1' : undefined}
                      value={contractData[field.key] ?? ''}
                      onChange={(e) => updateField(field.key, e.target.value)}
                      placeholder={`Enter ${field.label.toLowerCase()}`}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: `1px solid ${theme.colors.borders}`,
                        borderRadius: theme.radius.sm,
                        fontSize: '14px',
                        color: theme.colors.text.primary,
                        background: '#FFFFFF',
                      }}
                    />
                  </div>
                );
              })}

              {totalPossibleSupply !== null && (
                <div data-testid="contract-total-supply-banner" style={{
                  padding: '12px 14px',
                  background: '#F0F9FF',
                  border: '1px solid #38BDF8',
                  borderRadius: theme.radius.sm,
                  fontSize: 13,
                  color: '#0C4A6E',
                  lineHeight: 1.5,
                }}>
                  <strong>Total Possible Supply: {totalPossibleSupply.toLocaleString()}</strong>
                  <div style={{ fontSize: 12, color: '#075985', marginTop: 2 }}>
                    Total Possible Supply = Number of Token IDs × Max Supply Per ID.
                  </div>
                </div>
              )}

              {/* Advanced / Technical Details — Base URI is guided by
                  default and hidden from the primary form. Advanced users
                  may override; blank means auto-generate. */}
              {template.fields.some(f => f.key === 'base_uri') && (
                <details data-testid="contract-advanced-details" style={{
                  border: `1px solid ${theme.colors.borders}`,
                  borderRadius: theme.radius.sm,
                  padding: '10px 14px',
                  background: '#FAFAFA',
                }}>
                  <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600, color: theme.colors.text.primary }}>
                    Advanced / Technical Details
                  </summary>
                  <div style={{ marginTop: 12 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: theme.colors.text.primary, marginBottom: 6 }}>
                      Base URI (Metadata)
                    </label>
                    <p style={{ fontSize: 12, color: theme.colors.text.secondary, marginTop: 0, marginBottom: 6, lineHeight: 1.5 }}>
                      Leave blank for guided mode — Kickback Contract will generate a valid metadata URI using this Kickback's data and uploaded artwork. Only fill in if you host metadata yourself.
                    </p>
                    <input
                      data-testid="contract-field-base_uri"
                      type="text"
                      value={contractData.base_uri ?? ''}
                      onChange={(e) => updateField('base_uri', e.target.value)}
                      placeholder="Auto-generated (recommended)"
                      style={{
                        width: '100%', padding: '10px',
                        border: `1px solid ${theme.colors.borders}`,
                        borderRadius: theme.radius.sm,
                        fontSize: '14px',
                        color: theme.colors.text.primary,
                        background: '#FFFFFF',
                      }}
                    />
                  </div>
                </details>
              )}
            </div>

            <button
              data-testid="smart-contract-generate-btn"
              onClick={handleGenerate}
              disabled={!isComplete() || generating}
              style={{
                width: '100%',
                marginTop: '24px',
                padding: '12px',
                background:
                  isComplete() && !generating ? theme.colors.primary : theme.colors.bg.tertiary,
                color: '#FFFFFF',
                border: 'none',
                borderRadius: theme.radius.sm,
                fontSize: '14px',
                fontWeight: 600,
                cursor: isComplete() && !generating ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              <Check size={16} />
              {generating
                ? existingDraftId
                  ? 'Saving…'
                  : 'Generating…'
                : existingDraftId
                ? 'Save / Update Contract Draft'
                : 'Generate Contract Draft'}
            </button>
            {genErr && (
              <p
                data-testid="smart-contract-error"
                style={{
                  marginTop: 10,
                  fontSize: 12,
                  fontWeight: 600,
                  color: theme.colors.status.error,
                }}
              >
                {genErr}
              </p>
            )}
            {genSuccess && !genErr && (
              <div
                data-testid="smart-contract-success"
                style={{
                  marginTop: 12,
                  padding: '10px 14px',
                  background: '#ECFDF5',
                  border: '1px solid #10B981',
                  borderRadius: theme.radius.sm,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <Check size={16} style={{ color: '#047857' }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#065F46' }}>Contract Draft Ready</div>
                  <div style={{ fontSize: 12, color: '#065F46' }}>
                    Your contract configuration has been saved and the contract draft is ready for review.
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SmartContractWizard;
